import { Message } from 'models';
import { ITranslationVariables, globalContentUtil } from 'utils';
import { isValidEmail } from 'utils/valid-email.util';

import { PaymentArrangementPaymentMethodEnum } from '../../api/payment-arrangement.types';
import { isValidAllCountriesIBAN } from './validators/iban';

type validatorReturnType = undefined | Message;

const translateIfMessage = (message?: Message) =>
  message && globalContentUtil.translateToString(message.translationKey, message.value as ITranslationVariables);

/**
 * Method to combine all validators.
 * Returns first error that appears as string.
 */
export const composeValidators =
  (passedValidators: Array<(inputValue: unknown) => validatorReturnType>) => (inputValue: unknown) =>
    passedValidators.reduce((error, validator) => error || translateIfMessage(validator(inputValue)), undefined);

export const checkPassword = (password: string) => ({
  length: password.length >= 8,
  upperCase: /[A-Z]+/.test(password),
  lowerCase: /[a-z]+/.test(password),
  number: /[0-9]+/.test(password),
  specialCharacter: /[!?€><;:|\\~@#$%^&*)(+=._{}-]+/.test(password),
});

const cleanTelephoneNumber = (phoneNumber: string) => {
  const cleanRegexp = /[+.-\s]+/gi;
  return phoneNumber.replace(cleanRegexp, '');
};

/**
 * Object with all validators available.
 */
export const validators = {
  // Required.
  required: (value: Array<unknown> | string | number | boolean | null | undefined): validatorReturnType => {
    if (value && typeof value === 'object') {
      return value.length > 0 ? undefined : { translationKey: 'error.validation.required' };
    }

    return value ? undefined : { translationKey: 'error.validation.required' };
  },

  // Accept terms.
  acceptTerms: (value: Array<unknown> | string | number): validatorReturnType => {
    if (value && typeof value === 'object') {
      return value.length > 0 ? undefined : { translationKey: 'error.validation.acceptTerms' };
    }

    return value ? undefined : { translationKey: 'error.validation.acceptTerms' };
  },

  // Checks for number or null/undefined
  number: (value: Array<unknown> | string | number | boolean | null | undefined | object): validatorReturnType => {
    // null and undefined are valid values. Let those be handled by required.
    return (!isNaN(parseFloat(String(value))) && isFinite(Number(value))) || value === undefined || value === null
      ? undefined
      : { translationKey: 'error.validation.number' };
  },

  // Accepts an integer as minimum value it should match, which returns a new validator function.
  minValue:
    (min: number): ((value: number) => void) =>
    (value: number): validatorReturnType =>
      isNaN(value) || value >= min ? undefined : { translationKey: 'error.validation.min-value', value: min },

  // Accepts an integer as minimum number of items that should be in array. Returns new validator function.
  minItemsSelected:
    (min: number): ((value: Array<unknown>) => validatorReturnType) =>
    (value: Array<unknown>): validatorReturnType =>
      value && value.length >= min ? undefined : { translationKey: 'error.validation.min-items-selected', value: min },

  // Checks if the given value match with the email regexp
  email: (value: string | undefined): validatorReturnType => {
    if (!value) {
      return undefined;
    }
    return isValidEmail(value) ? undefined : { translationKey: 'error.validation.email' };
  },

  // Checks only the username in username@domain.nl
  emailUserName: (value: string | undefined): validatorReturnType => {
    if (!value) return undefined;

    if (/^[a-z0-9_][a-z0-9_-]*(\.[a-z0-9_-]+)*$/i.test(value)) {
      return undefined;
    }

    return { translationKey: 'error.validation.emailUserName' };
  },

  noSpecialCharacters: (value: string | undefined): validatorReturnType => {
    if (!value) return undefined;

    if (/^[a-z0-9-'`\s]*$/i.test(value)) {
      return undefined;
    }

    return { translationKey: 'error.validation.noSpecialCharacters' };
  },

  emailUserNameMaxLength: (value: string | undefined): validatorReturnType => {
    if (!value) return undefined;

    return value.length > 50 ? { translationKey: 'error.validation.emailUserNameMaxLength' } : undefined;
  },

  emailFirstNameMaxLength: (value: string | undefined): validatorReturnType => {
    if (!value) return undefined;

    return value.length > 50 ? { translationKey: 'error.validation.emailFirstNameMaxLength' } : undefined;
  },

  emailLastNameMaxLength: (value: string | undefined): validatorReturnType => {
    if (!value) return undefined;

    return value.length > 50 ? { translationKey: 'error.validation.emailLastNameMaxLength' } : undefined;
  },

  // Checks if the given value match with the zipcode regexp
  zipcode: (value: string | undefined): validatorReturnType => {
    if (!value) {
      return undefined;
    }
    // strip it of all spaces
    const spacelessZipcode = value.replace(/ /g, '');
    const regexpNL = /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i;
    const regexDE = /^[0-9]{5}$/;
    const regexBE = /^[0-9]{4}$/;
    const validZipcode =
      regexpNL.test(spacelessZipcode) || regexDE.test(spacelessZipcode) || regexBE.test(spacelessZipcode);
    return validZipcode ? undefined : { translationKey: 'error.validation.zipcode' };
  },

  // Check if the given value is a valid IBAN with any SEPA country code
  ibanAll: (value: string | undefined): validatorReturnType => isValidAllCountriesIBAN(value),

  // Checks if the given value match with 06 mobile numbers
  mobile: (value: string | undefined) => {
    if (!value) {
      return undefined;
    }

    const regexp = /^[0-9]+$/;
    const mobile = cleanTelephoneNumber(value);

    if (mobile.startsWith('06') && regexp.test(mobile) && mobile.length === 10) {
      return undefined;
    }

    return { translationKey: 'error.validation.mobile' };
  },

  fixed: (value: string | undefined) => {
    if (!value) {
      return undefined;
    }

    const regexp = /^[0-9]+$/;
    const fixed = cleanTelephoneNumber(value);

    if (fixed.startsWith('06') || fixed.length !== 10 || !regexp.test(fixed)) {
      return { translationKey: 'error.validation.fixed' };
    }

    return undefined;
  },

  // Checks if the given value match a phone-number
  phone: (value: string | undefined) => {
    if (!value) {
      return undefined;
    }

    const numberRegexp = /^[0-9]{7,20}$/gi;
    const phone = cleanTelephoneNumber(value);
    // After that, test if the remaining chars are all digits
    if (numberRegexp.test(phone)) return undefined;

    return { translationKey: 'error.validation.phone' };
  },

  // Checks if the given value matches the password policy
  password: (value: string) => {
    if (!value) {
      return undefined;
    }

    const passwordChecks = checkPassword(value);

    if (
      passwordChecks.length &&
      passwordChecks.lowerCase &&
      passwordChecks.upperCase &&
      passwordChecks.specialCharacter &&
      passwordChecks.number
    ) {
      return undefined;
    }

    return { translationKey: 'error.validation.password' };
  },

  maxLength: (value: string | undefined) => {
    if (!value) {
      return undefined;
    }
    return value.length > 2000 ? { translationKey: `error.validation.maxLength` } : undefined;
  },

  kvkLength: (value: string | undefined): validatorReturnType => {
    if (!value) {
      return undefined;
    }
    return value.length !== 8 ? { translationKey: `error.validation.kvkLength` } : undefined;
  },

  // Accepts a string as bankAccount value that compared with the given paymentMethod. Returns a new validator function.
  directDebitHasBankAccount:
    (bankAccount: string | undefined | null): ((value: string) => validatorReturnType) =>
    (value: string): validatorReturnType =>
      (bankAccount && value === PaymentArrangementPaymentMethodEnum.DIRECT_DEBIT) ||
      value === PaymentArrangementPaymentMethodEnum.CASH
        ? undefined
        : { translationKey: 'error.validation.DirectDebitBankAccountMissing' },
};
